import type { GameConfig } from "./config";
import { DEFAULT_CONFIG, TUTORIAL_BALLS, TUTORIAL_SNOW } from "./config";
import {
  createPlayerState,
  type PlayerState,
  type OpponentType,
} from "./player-state";
import { SeededRNG } from "./rng";
import type { InputAction } from "../input/types";

export type MatchPhase =
  | "intro"
  | "countdown"
  | "r1_gather"
  | "banner_r1"
  | "banner_r2"
  | "r2_roll"
  | "banner_r3"
  | "r3_throw"
  | "sudden_death"
  | "resolve"
  | "results";

export type MatchOutcome = "win" | "lose" | "draw";
export type WinType = "KO" | "decision" | "suddenDeath" | "forfeit";

export interface Projectile {
  id: number;
  perfect: boolean;
  thrower: "player" | "opponent";
  target: "player" | "opponent";
  startTick: number;
  endTick: number;
  lane: "left" | "center" | "right";
  damage: number;
}

export interface HitEffect {
  id: number;
  target: "player" | "opponent";
  startTick: number;
  damage: number;
  perfect: boolean;
}

export interface MatchState {
  phase: MatchPhase;
  tick: number;
  phaseStartTick: number;
  player: PlayerState;
  opponent: PlayerState;
  opponentType: OpponentType;
  playerLane: "left" | "center" | "right";
  opponentLane: "left" | "center" | "right";
  outcome: MatchOutcome | null;
  winType: WinType | null;
  paused: boolean;
  // Round-specific state
  playerRollProgress: number; // player's accumulated radians
  opponentRollProgress: number; // opponent's accumulated radians
  projectiles: Projectile[];
  hitEffects: HitEffect[];
  nextProjectileId: number;
  roundsWon?: [number, number];
}

export type MatchEvent =
  | { type: "matchStart" }
  | { type: "phaseChange"; phase: MatchPhase; timeLeft: number }
  | { type: "tick"; secondsLeft: number }
  | { type: "snow"; player: "player" | "opponent"; amount: number }
  | {
      type: "ammo";
      player: "player" | "opponent";
      count: number;
      perfect?: boolean;
    }
  | { type: "hp"; player: "player" | "opponent"; hp: number }
  | {
      type: "hit";
      player: "player" | "opponent";
      target: "player" | "opponent";
      damage: number;
      perfect: boolean;
    }
  | {
      type: "throw";
      player: "player" | "opponent";
      lane: string;
      perfect: boolean;
    }
  | { type: "knockout"; player: "player" | "opponent" }
  | { type: "tutorialComplete" }
  | { type: "dodged"; player: "player" | "opponent" }
  | { type: "blocked"; player: "player" | "opponent"; damage: number }
  | { type: "perfectBlock"; player: "player" | "opponent" }
  | { type: "dodgeActivated"; player: "player" | "opponent" }
  | { type: "blockActivated"; player: "player" | "opponent" }
  | { type: "matchResolved"; outcome: MatchOutcome; winType: WinType };

export function determineDecision(
  player: Pick<PlayerState, "damageDealt" | "hits">,
  opponent: Pick<PlayerState, "damageDealt" | "hits">,
): MatchOutcome | null {
  if (player.damageDealt !== opponent.damageDealt) {
    return player.damageDealt > opponent.damageDealt ? "win" : "lose";
  }
  if (player.hits !== opponent.hits) {
    return player.hits > opponent.hits ? "win" : "lose";
  }
  return null;
}

export type MatchEventListener = (event: MatchEvent) => void;

export class MatchController {
  private state: MatchState;
  private config: GameConfig;
  private rng: SeededRNG;
  private listeners: MatchEventListener[] = [];
  private playerStrokeTimes: number[] = []; // player gather rate tracking
  private opponentStrokeTimes: number[] = []; // bot gather rate tracking
  private accumulator = 0; // fixed timestep accumulator
  private nextBotGatherTick = 0;
  private nextBotRollTick = 0;
  private nextBotThrowTick = 0;
  private tutorialDone = false;

  constructor(config: GameConfig = DEFAULT_CONFIG, seed: number = 1234) {
    this.config = config;
    this.rng = new SeededRNG(seed);
    this.state = {
      phase: "intro",
      tick: 0,
      phaseStartTick: 0,
      player: createPlayerState(config.maxHP),
      opponent: createPlayerState(config.opponentHP ?? config.maxHP),
      opponentType: "bot",
      playerLane: "center",
      opponentLane: "center",
      outcome: null,
      winType: null,
      paused: false,
      playerRollProgress: 0,
      opponentRollProgress: 0,
      projectiles: [],
      hitEffects: [],
      nextProjectileId: 0,
      roundsWon: [0, 0],
    };
    // Leftover snow from the previous match (capped) starts in the jar.
    this.state.player.snow = Math.min(
      config.snowCap,
      Math.max(0, Math.min(config.carrySnowCap, config.carrySnow)),
    );
  }

  /**
   * Snow to carry into the next match: whatever is left in the jar after
   * Round 2, capped at carrySnowCap.
   */
  getCarrySnow(): number {
    return Math.min(
      this.config.carrySnowCap,
      Math.max(0, this.state.player.snow),
    );
  }

  on(listener: MatchEventListener): void {
    this.listeners.push(listener);
  }

  private emit(event: MatchEvent): void {
    this.listeners.forEach((l) => l(event));
  }

  getState(): Readonly<MatchState> {
    return this.state;
  }

  pause(): void {
    this.state.paused = true;
  }

  resume(): void {
    this.state.paused = false;
  }

  forfeit(): void {
    // Forfeit ends match immediately
    this.state.phase = "resolve";
    this.finishMatch("lose", "forfeit");
  }

  startMatch(): void {
    if (this.config.tutorial) {
      // Practice goes straight to the scooping step: no countdown or banners.
      this.advanceToPhase("r1_gather", 0);
      this.emit({ type: "matchStart" });
      return;
    }
    this.state.phase = "countdown";
    this.state.phaseStartTick = this.state.tick;
    this.emit({
      type: "phaseChange",
      phase: "countdown",
      timeLeft: this.config.countdownDuration,
    });
    this.emit({ type: "matchStart" });
  }

  qaBeforeTick?: (tick: number) => void;
  qaAfterTick?: () => void;

  update(deltaMs: number): void {
    if (this.state.paused) return;

    // Fixed timestep accumulator: tick at exactly 60Hz regardless of frame rate
    const fixedDelta = 1000 / this.config.tickRate; // 16.666ms per tick at 60Hz
    this.accumulator += deltaMs;

    // Clamp accumulator to prevent spiral of death
    if (this.accumulator > fixedDelta * 10) {
      this.accumulator = fixedDelta * 10;
    }

    // Tick at fixed intervals
    while (this.accumulator + 1e-9 >= fixedDelta) {
      this.qaBeforeTick?.(this.state.tick + 1);
      this.tick();
      this.qaAfterTick?.();
      this.accumulator = Math.max(0, this.accumulator - fixedDelta);
    }
  }

  private tick(): void {
    this.state.tick++;
    const elapsed =
      (this.state.tick - this.state.phaseStartTick) / this.config.tickRate;

    // Update projectiles and check for impacts
    this.updateProjectiles();

    // Clean up old hit effects (0.3s duration)
    const hitEffectDuration = 0.3 * this.config.tickRate;
    this.state.hitEffects = this.state.hitEffects.filter(
      (effect) => this.state.tick - effect.startTick < hitEffectDuration,
    );

    if (this.config.tutorial) {
      this.tutorialTick();
      const scripted = this.tutorialBotAction();
      if (scripted) this.processBotAction(scripted);
      return;
    }

    switch (this.state.phase) {
      case "intro":
        // Wait for startMatch() to be called
        break;

      case "countdown":
        if (elapsed >= this.config.countdownDuration) {
          this.advanceToPhase("banner_r1", this.config.bannerDuration);
        } else if (elapsed >= this.config.countdownDuration - 3) {
          const secondsLeft = Math.ceil(
            this.config.countdownDuration - elapsed,
          );
          this.emit({ type: "tick", secondsLeft });
        }
        break;

      case "banner_r1":
        if (elapsed >= this.config.bannerDuration) {
          this.advanceToPhase("r1_gather", this.config.roundGatherDuration);
        }
        break;

      case "r1_gather":
        if (elapsed >= this.config.roundGatherDuration) {
          if (this.state.player.snow !== this.state.opponent.snow)
            this.state.roundsWon![
              this.state.player.snow > this.state.opponent.snow ? 0 : 1
            ]++;
          this.advanceToPhase("banner_r2", this.config.bannerDuration);
        } else if (this.config.roundGatherDuration - elapsed <= 3) {
          const secondsLeft = Math.ceil(
            this.config.roundGatherDuration - elapsed,
          );
          this.emit({ type: "tick", secondsLeft });
        }
        break;

      case "banner_r2":
        if (elapsed >= this.config.bannerDuration) {
          this.advanceToPhase("r2_roll", this.config.roundRollDuration);
        }
        break;

      case "r2_roll":
        if (elapsed >= this.config.roundRollDuration) {
          const playerAmmo = this.state.player.ammo;
          const opponentAmmo = this.state.opponent.ammo;
          if (playerAmmo !== opponentAmmo)
            this.state.roundsWon![playerAmmo > opponentAmmo ? 0 : 1]++;
          this.advanceToPhase("banner_r3", this.config.bannerDuration);
        } else if (this.config.roundRollDuration - elapsed <= 3) {
          const secondsLeft = Math.ceil(
            this.config.roundRollDuration - elapsed,
          );
          this.emit({ type: "tick", secondsLeft });
        }
        break;

      case "banner_r3":
        if (elapsed >= this.config.bannerDuration) {
          this.advanceToPhase("r3_throw", this.config.roundThrowDuration);
        }
        break;

      case "r3_throw":
        if (elapsed >= this.config.roundThrowDuration) {
          this.advanceToPhase("resolve", 0);
          this.resolveMatch();
        } else if (this.config.roundThrowDuration - elapsed <= 3) {
          const secondsLeft = Math.ceil(
            this.config.roundThrowDuration - elapsed,
          );
          this.emit({ type: "tick", secondsLeft });
        }
        break;

      case "sudden_death":
        if (elapsed >= this.config.suddenDeathDuration) {
          this.finishMatch("draw", "suddenDeath");
        } else if (this.config.suddenDeathDuration - elapsed <= 3) {
          this.emit({
            type: "tick",
            secondsLeft: Math.ceil(this.config.suddenDeathDuration - elapsed),
          });
        }
        break;

      case "resolve":
        // Match is resolved, wait for UI
        break;

      case "results":
        // Final screen
        break;
    }

    const botAction = this.generateBotAction();
    if (botAction) {
      this.processBotAction(botAction);
    }
  }

  /** Tutorial: rounds end on success, never on a clock. */
  private tutorialTick(): void {
    const { player, opponent } = this.state;
    switch (this.state.phase) {
      case "r1_gather":
        if (player.snow >= TUTORIAL_SNOW) this.advanceToPhase("r2_roll", 0);
        break;
      case "r2_roll":
        if (player.ammo >= TUTORIAL_BALLS) {
          opponent.ammo = 1; // Mika's one slow ball
          this.advanceToPhase("r3_throw", 0);
        }
        break;
      case "r3_throw":
        // No fail state: never leave the player without a ball to throw.
        if (
          !this.tutorialDone &&
          player.ammo <= 0 &&
          opponent.hp > 0 &&
          !this.state.projectiles.some((p) => p.thrower === "player")
        )
          player.ammo = 1;
        break;
    }
  }

  private tutorialBotAction(): InputAction | null {
    const s = this.state;
    if (
      this.tutorialDone ||
      s.phase !== "r3_throw" ||
      s.opponent.ammo <= 0 ||
      s.tick < s.phaseStartTick + 150
    )
      return null;
    return {
      type: "throw",
      lane: s.playerLane,
      power: 0.5,
      tick: s.tick,
    };
  }

  private advanceToPhase(phase: MatchPhase, duration: number): void {
    this.state.phase = phase;
    this.state.phaseStartTick = this.state.tick;
    this.emit({ type: "phaseChange", phase, timeLeft: duration });
  }

  private resolveMatch(): void {
    // Decision on remaining HP% (the ladder gives snowmen different max HP),
    // then on hits; a full tie goes to sudden death.
    const playerFrac = this.state.player.hp / this.config.maxHP;
    const opponentFrac =
      this.state.opponent.hp / (this.config.opponentHP ?? this.config.maxHP);
    const outcome: MatchOutcome | null =
      Math.abs(playerFrac - opponentFrac) > 1e-9
        ? playerFrac > opponentFrac
          ? "win"
          : "lose"
        : determineDecision(this.state.player, this.state.opponent);
    if (outcome) {
      this.finishMatch(outcome, "decision");
      return;
    }

    this.ensureSuddenDeathAmmo(this.state.player);
    this.ensureSuddenDeathAmmo(this.state.opponent);
    this.advanceToPhase("sudden_death", this.config.suddenDeathDuration);
  }

  private ensureSuddenDeathAmmo(player: PlayerState): void {
    // Sudden death needs a ball to settle the match. This is the only time
    // ammo is granted after Round 2.
    if (player.ammo === 0) {
      player.ammo = 1;
    }
  }

  private finishMatch(outcome: MatchOutcome, winType: WinType): void {
    if (this.state.phase === "results") return;
    this.state.outcome = outcome;
    this.state.winType = winType;
    this.advanceToPhase("results", 0);
    this.emit({ type: "matchResolved", outcome, winType });
  }

  processPlayerAction(action: InputAction): void {
    switch (action.type) {
      case "gatherStroke":
        this.handleGatherStroke("player", action.tick / this.config.tickRate);
        break;
      case "rollLap":
        this.state.playerRollProgress += 2 * Math.PI;
        this.handleRollLap("player", action);
        break;
      case "throw":
        this.handleThrow("player", action.lane, action.tick);
        break;
      case "dodge":
        if (this.handleDodge("player")) {
          this.state.playerLane = this.adjacentLane(
            this.state.playerLane,
            action.direction,
          );
        }
        break;
      case "block":
        this.handleBlock("player");
        break;
    }
  }

  private handleDodge(who: "player" | "opponent"): boolean {
    if (this.state.phase !== "r3_throw" && this.state.phase !== "sudden_death")
      return false;

    const ps = who === "player" ? this.state.player : this.state.opponent;

    // Check if on cooldown
    if (this.state.tick < ps.dodgeCooldownUntil) {
      return false; // Still on cooldown
    }

    // Activate dodge
    const iFramesTicks =
      this.config.dodgeIFramesDuration * this.config.tickRate;
    const cooldownTicks = this.config.dodgeCooldown * this.config.tickRate;
    ps.dodgeCooldownUntil = this.state.tick + iFramesTicks + cooldownTicks;

    this.emit({ type: "dodgeActivated", player: who });
    return true;
  }

  private adjacentLane(
    lane: "left" | "center" | "right",
    direction: "left" | "right",
  ): "left" | "center" | "right" {
    const lanes = ["left", "center", "right"] as const;
    const current = lanes.indexOf(lane);
    const offset = direction === "left" ? -1 : 1;
    return lanes[Math.max(0, Math.min(lanes.length - 1, current + offset))]!;
  }

  private handleBlock(who: "player" | "opponent"): void {
    if (this.state.phase !== "r3_throw" && this.state.phase !== "sudden_death")
      return;

    const ps = who === "player" ? this.state.player : this.state.opponent;

    // Check if on cooldown
    if (this.state.tick < ps.blockCooldownUntil) {
      return; // Still on cooldown
    }

    // Activate block
    const windowTicks = this.config.blockWindowDuration * this.config.tickRate;
    const cooldownTicks = this.config.blockCooldown * this.config.tickRate;
    ps.blockCooldownUntil = this.state.tick + windowTicks + cooldownTicks;

    this.emit({ type: "blockActivated", player: who });
  }

  private handleGatherStroke(
    who: "player" | "opponent",
    timeSeconds: number,
  ): void {
    if (this.state.phase !== "r1_gather") return;

    const ps = who === "player" ? this.state.player : this.state.opponent;
    const strokeTimes =
      who === "player" ? this.playerStrokeTimes : this.opponentStrokeTimes;

    // Track stroke rate (kept for the anti-cheat/QA readouts)
    const filtered = strokeTimes.filter((t) => timeSeconds - t < 1.0);
    filtered.push(timeSeconds);
    if (who === "player") {
      this.playerStrokeTimes = filtered;
    } else {
      this.opponentStrokeTimes = filtered;
    }

    // R1 snow = min(snowCap, snowPerStroke x strokes)
    // Ladder snowmen build exactly their ball budget (botBalls x 10 snow),
    // which can exceed what the player's jar holds.
    const cap =
      who === "opponent" && this.config.botBalls
        ? this.config.botBalls * this.config.snowPerBall
        : this.config.snowCap;
    ps.snow = Math.min(cap, ps.snow + this.config.snowPerStroke);
    this.emit({ type: "snow", player: who, amount: ps.snow });
  }

  private handleRollLap(
    who: "player" | "opponent",
    lap: { lapSeconds?: number; round?: boolean } = {},
  ): void {
    if (this.state.phase !== "r2_roll") return;

    // Each closed lap packs one ball out of snowPerBall snow. With no snow
    // left the lap packs nothing, so balls = min(floor(snow / 10), laps).
    const ps = who === "player" ? this.state.player : this.state.opponent;
    if (ps.snow < this.config.snowPerBall) return;

    const perfect =
      lap.round === true &&
      lap.lapSeconds !== undefined &&
      lap.lapSeconds <= this.config.perfectMaxSeconds;
    ps.snow -= this.config.snowPerBall;
    ps.ammo++;
    if (perfect) ps.perfectBalls++;
    this.emit({ type: "ammo", player: who, count: ps.ammo, perfect });
  }

  private handleThrow(
    who: "player" | "opponent",
    lane: "left" | "center" | "right",
    _tick: number,
  ): void {
    if (this.state.phase !== "r3_throw" && this.state.phase !== "sudden_death")
      return;

    const ps = who === "player" ? this.state.player : this.state.opponent;
    const targetName = who === "player" ? "opponent" : "player";

    // No snowballs left: the throw is refused.
    if (ps.ammo <= 0) return;
    ps.ammo--;
    // PERFECT PACK balls are thrown first.
    const perfect = ps.perfectBalls > 0;
    if (perfect) ps.perfectBalls--;
    ps.throws++;

    // Emit throw event for audio
    this.emit({ type: "throw", player: who, lane, perfect });

    const flightTicks = this.config.projectileFlightTime * this.config.tickRate;
    const damage = this.getDamage(perfect);

    this.state.projectiles.push({
      id: this.state.nextProjectileId++,
      perfect,
      thrower: who,
      target: targetName,
      startTick: this.state.tick,
      endTick: this.state.tick + flightTicks,
      lane,
      damage,
    });
  }

  private updateProjectiles(): void {
    const toRemove: number[] = [];

    for (let i = 0; i < this.state.projectiles.length; i++) {
      const proj = this.state.projectiles[i];
      if (!proj) continue;

      // Check if projectile has reached target
      if (this.state.tick >= proj.endTick) {
        this.resolveProjectileHit(proj);
        toRemove.push(i);
      }
    }

    // Remove landed projectiles
    for (let i = toRemove.length - 1; i >= 0; i--) {
      const idx = toRemove[i];
      if (idx !== undefined) {
        this.state.projectiles.splice(idx, 1);
      }
    }
  }

  private resolveProjectileHit(proj: Projectile): void {
    const target =
      proj.target === "player" ? this.state.player : this.state.opponent;
    const thrower =
      proj.thrower === "player" ? this.state.player : this.state.opponent;
    const targetLane =
      proj.target === "player"
        ? this.state.playerLane
        : this.state.opponentLane;

    if (proj.lane !== targetLane) {
      this.emit({ type: "dodged", player: proj.target });
      return;
    }

    // Snowmen on the ladder sometimes dodge the player's ball outright.
    const botDodge = this.config.botDodgeChance ?? 0;
    if (
      proj.target === "opponent" &&
      botDodge > 0 &&
      this.rng.next() < botDodge
    ) {
      this.emit({ type: "dodged", player: "opponent" });
      return;
    }

    // Check dodge i-frames (0.4s)
    const isDodging =
      this.state.tick <
      target.dodgeCooldownUntil -
        this.config.dodgeCooldown * this.config.tickRate;

    if (isDodging) {
      this.emit({ type: "dodged", player: proj.target });
      return;
    }

    // Check block window (0.6s)
    const blockWindowTicks =
      this.config.blockWindowDuration * this.config.tickRate;
    const isBlocking =
      this.state.tick <
      target.blockCooldownUntil -
        this.config.blockCooldown * this.config.tickRate;

    let damage = proj.damage;
    let perfectBlock = false;

    if (isBlocking) {
      damage = 0; // a block stops the snowball completely

      // Perfect block: blocked within first 0.2s of window
      const blockActiveFor =
        target.blockCooldownUntil -
        this.config.blockCooldown * this.config.tickRate -
        this.state.tick;
      if (blockActiveFor <= blockWindowTicks - 0.2 * this.config.tickRate) {
        perfectBlock = true;
      }

      target.blocks++;
      this.emit({ type: "blocked", player: proj.target, damage });
      if (perfectBlock) {
        this.emit({ type: "perfectBlock", player: proj.target });
      }
    }

    if (damage <= 0) {
      return;
    }
    // Practice: the snowman's ball never hurts.
    if (this.config.tutorial && proj.target === "player") return;

    // Apply damage. A blocked zero-damage projectile is not a landed hit.
    target.hp = Math.max(0, target.hp - damage);
    thrower.hits++;
    thrower.damageDealt += damage;

    // Spawn hit effect
    this.state.hitEffects.push({
      id: this.state.nextProjectileId++,
      target: proj.target,
      startTick: this.state.tick,
      damage,
      perfect: proj.perfect,
    });

    this.emit({
      type: "hit",
      player: proj.thrower,
      target: proj.target,
      damage,
      perfect: proj.perfect,
    });
    this.emit({ type: "hp", player: proj.target, hp: target.hp });

    if (this.state.phase === "sudden_death") {
      this.finishMatch(
        proj.thrower === "player" ? "win" : "lose",
        "suddenDeath",
      );
      return;
    }

    // Check for knockout
    if (this.config.tutorial && target.hp <= 0) {
      this.tutorialDone = true;
      this.emit({ type: "knockout", player: proj.target });
      this.emit({ type: "tutorialComplete" });
      return;
    }
    if (target.hp <= 0) {
      const outcome = proj.thrower === "player" ? "win" : "lose";
      this.emit({ type: "knockout", player: proj.target });
      this.finishMatch(outcome, "KO");
    }
  }

  private getDamage(perfect: boolean): number {
    return this.config.ballDamage + (perfect ? this.config.perfectBonus : 0);
  }

  generateBotAction(): InputAction | null {
    const phase = this.state.phase;
    const reactionTicks = () =>
      Math.round(
        this.rng.nextFloat(
          this.config.bot.reactionMin,
          this.config.bot.reactionMax,
        ) * this.config.tickRate,
      );

    if (phase === "r1_gather") {
      if (this.nextBotGatherTick === 0) {
        this.nextBotGatherTick = this.state.phaseStartTick + reactionTicks();
      }
      if (this.state.tick >= this.nextBotGatherTick) {
        this.nextBotGatherTick =
          this.state.tick +
          Math.round(this.config.bot.gatherInterval * this.config.tickRate);
        return {
          type: "gatherStroke",
          speed: 1,
          tick: this.state.tick,
        };
      }
    } else if (phase === "r2_roll") {
      if (this.nextBotRollTick === 0) {
        this.nextBotRollTick = this.state.phaseStartTick + reactionTicks();
      }
      if (this.state.tick >= this.nextBotRollTick) {
        this.nextBotRollTick =
          this.state.tick +
          Math.round(this.config.bot.rollInterval * this.config.tickRate);
        return {
          type: "rollLap",
          tick: this.state.tick,
        };
      }
    } else if (phase === "r3_throw" || phase === "sudden_death") {
      if (this.nextBotThrowTick === 0) {
        this.nextBotThrowTick = this.state.phaseStartTick + reactionTicks();
      }
      if (
        this.state.tick >= this.nextBotThrowTick &&
        this.state.opponent.ammo > 0
      ) {
        this.nextBotThrowTick =
          this.state.tick +
          Math.round(this.config.bot.throwInterval * this.config.tickRate);
        const misses = (["left", "center", "right"] as const).filter(
          (lane) => lane !== this.state.playerLane,
        );
        const lane =
          this.rng.next() < this.config.bot.accuracy
            ? this.state.playerLane
            : misses[this.rng.nextInt(0, misses.length - 1)]!;
        return {
          type: "throw",
          lane,
          power: this.rng.nextFloat(0.5, 1.0),
          tick: this.state.tick,
        };
      }
    }

    return null;
  }

  processBotAction(action: InputAction): void {
    switch (action.type) {
      case "gatherStroke":
        this.handleGatherStroke("opponent", action.tick / this.config.tickRate);
        break;
      case "rollLap":
        this.state.opponentRollProgress += 2 * Math.PI;
        this.handleRollLap("opponent", action);
        break;
      case "throw":
        this.handleThrow("opponent", action.lane, action.tick);
        break;
    }
  }
}
